import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import API_BASE_URL from './config';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Adicionar estilos CSS para animação
const modalStyles = `
  @keyframes slideInRight {
    from { 
      opacity: 0; 
      transform: translateX(100%); 
    }
    to { 
      opacity: 1; 
      transform: translateX(0); 
    }
  }
`;

const bancosFixos = [
  { nome: 'Sicoob', url: 'https://i.imgur.com/8uiOlmS.png' },
  { nome: 'Santander', url: 'https://i.imgur.com/VCxykJQ.png' },
  { nome: 'Caixa', url: 'https://i.imgur.com/KaYnbWE.png' },
  { nome: 'Bradesco', url: 'https://i.imgur.com/6xdg3zN.png' },
  { nome: 'Brasil', url: 'https://i.imgur.com/EJ1Nhf3.png' },
];

function formatMilhar(valor) {
  if (valor === '' || isNaN(Number(valor))) return '';
  const numero = Number(valor).toFixed(2);
  return Number(numero).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatSacas(valor) {
  if (valor === null || valor === undefined || isNaN(Number(valor))) return 'Aguarde';
  const numero = Math.round(Number(valor));
  return numero.toLocaleString('pt-BR');
}

function formatPercentual(valor) {
  if (valor === '' || isNaN(Number(valor))) return '';
  let numero = parseFloat(valor);
  if (numero > 100) numero = 100;
  if (numero < 0) numero = 0;
  return numero.toString();
}

// Função para formatar prazo baseado no plano
function formatarPrazo(prazo, plano) {
  if (!prazo || !plano) return `${prazo || 0} meses`;
  
  if (plano === 'Semestral') {
    const semestres = prazo / 6;
    return `${semestres.toFixed(2)} semestres`;
  } else if (plano === 'Anual') {
    const anos = prazo / 12;
    return `${anos.toFixed(2)} anos`;
  } else {
    return `${prazo} meses`;
  }
}

// Função para calcular valores corrigidos do recurso próprio
function calcularValoresCorrigidos(valorRecursoProprio) {
  const valores = [];
  for (let mes = 1; mes <= 12; mes++) {
    const valorCorrigido = valorRecursoProprio * (1 + 0.020 * mes);
    valores.push({
      mes: mes * 30, // Nomear como 30, 60, 90, etc.
      valorCorrigido: valorCorrigido
    });
  }
  return valores;
}

// Função para calcular valor do recurso próprio ajustado com entrada em dinheiro
function calcularValorRecursoProprioAjustado(valorRecursoProprio, valorDigitado, percentualEntrada, informarEntrada) {
  if (!informarEntrada || !percentualEntrada || isNaN(percentualEntrada)) {
    return valorRecursoProprio;
  }
  
  const percentual = parseFloat(percentualEntrada) / 100;
  const valorEntrada = valorDigitado * percentual;
  const valorAjustado = valorRecursoProprio - valorEntrada;
  
  return Math.max(0, valorAjustado); // Garantir que não seja negativo
}

// Função para registrar simulação no Google Sheets
async function registrarSimulacao(valorPedido) {
  try {
    const googleSheetsUrl = process.env.REACT_APP_GOOGLE_SIMULACOES;
    
    if (!googleSheetsUrl) {
      return;
    }

    const agora = new Date();
    const dataHora = agora.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });

    const dados = {
      values: [[dataHora, valorPedido]]
    };

    await fetch(googleSheetsUrl, {
      method: 'POST',
      mode: 'no-cors',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(dados)
    });

  } catch (error) {
    // Não bloqueia a simulação se houver erro
    console.error('Erro ao registrar simulação:', error);
  }
}

// Função para calcular valores da Operação CPR
function calcularOperacaoCPR(valorRecursoProprio) {
  const taxaJurosAnual = 0.1725;
  const taxaJurosMensal = Math.pow(1 + taxaJurosAnual, 1 / 12) - 1;
  const mesesCarencia = 5;
  const numeroParcelas = 3;
  const dataBase = new Date();
  const principal = valorRecursoProprio / numeroParcelas;

  let saldoInicial = valorRecursoProprio;
  let dataAtual = new Date(dataBase);
  dataAtual.setDate(dataAtual.getDate() + 31); // Primeira data

  const resultados = [];
  let jurosAcumulados = 0;
  let parcelasPagas = 0;
  const mesesTotais = mesesCarencia + (numeroParcelas - 1) * 12 + 1; // total até última parcela

  for (let mes = 1; mes <= mesesTotais; mes++) {
    const jurosMes = saldoInicial * taxaJurosMensal;
    jurosAcumulados += jurosMes;

    let amortizacao = 0;
    let principalMes = 0;

    const mesPagamento =
      mes === mesesCarencia + 1 ||
      mes === mesesCarencia + 1 + 12 ||
      mes === mesesCarencia + 1 + 24;

    if (mesPagamento) {
      amortizacao = jurosAcumulados + principal;
      principalMes = principal;
      jurosAcumulados = 0; // zera para próxima contagem
      parcelasPagas += 1;
    }

    const saldoFinal = saldoInicial + jurosMes - amortizacao;

    resultados.push({
      data: new Date(dataAtual),
      saldoInicial: saldoInicial,
      jurosMes: jurosMes,
      amortizacao: amortizacao,
      principal: principalMes,
      saldoFinal: saldoFinal
    });

    saldoInicial = saldoFinal;
    dataAtual.setDate(dataAtual.getDate() + 30);
  }

  return resultados;
}

// Condições hardcoded baseadas na tabela condicoes_consorcio
const condicoesHardcoded = {
  padrao: [
    {
      banco: 'Sicoob',
      prazo: 96,
      taxa: 0.2050,
      lance_sugerido: 0.6500,
      embutido: 0.0000,
      plano: 'Mensal / Semestral',
      atende: 'Correntista',
      possui_lance_embutido: 'Não',
      carta_contemplada: 'Não'
    },
    {
      banco: 'Santander',
      prazo: 44,
      taxa: 0.1650,
      lance_sugerido: 0.6500,
      embutido: 0.0000,
      plano: 'Mensal',
      atende: 'Correntista / Não correntista',
      possui_lance_embutido: 'Sim',
      carta_contemplada: 'Sim'
    },
    {
      banco: 'Brasil',
      prazo: 78,
      taxa: 0.1752,
      lance_sugerido: 0.5900,
      embutido: 0.0000,
      plano: 'Mensal / Semestral / Anual',
      atende: 'Correntista / Não correntista',
      possui_lance_embutido: 'Sim',
      carta_contemplada: 'Não'
    },
    {
      banco: 'Caixa',
      prazo: 100,
      taxa: 0.1850,
      lance_sugerido: 0.6862,
      embutido: 0.0000,
      plano: 'Mensal / Semestral',
      atende: 'Correntista / Não correntista',
      possui_lance_embutido: 'Sim',
      carta_contemplada: 'Não'
    },
    {
      banco: 'Bradesco',
      prazo: 40,
      taxa: 0.1550,
      lance_sugerido: 0.5700,
      embutido: 0.0000,
      plano: 'Mensal / Semestral',
      atende: 'Correntista / Não correntista',
      possui_lance_embutido: 'Não',
      carta_contemplada: 'Sim'
    }
  ],
  menor_entrada: [
    {
      banco: 'Santander',
      prazo: 44,
      taxa: 0.1650,
      lance_sugerido: 0.6288,
      embutido: 0.3198,
      plano: 'Mensal',
      atende: 'Correntista / Não correntista',
      possui_lance_embutido: 'Sim',
      carta_contemplada: 'Sim'
    },
    {
      banco: 'Brasil',
      prazo: 78,
      taxa: 0.1752,
      lance_sugerido: 0.5900,
      embutido: 0.2900,
      plano: 'Mensal / Semestral / Anual',
      atende: 'Correntista / Não correntista',
      possui_lance_embutido: 'Sim',
      carta_contemplada: 'Não'
    },
    {
      banco: 'Caixa',
      prazo: 100,
      taxa: 0.1850,
      lance_sugerido: 0.6500,
      embutido: 0.3000, 
      plano: 'Mensal / Semestral',
      atende: 'Correntista / Não correntista',
      possui_lance_embutido: 'Sim',
      carta_contemplada: 'Não'
    }
  ]
};

// Função para simular localmente sem backend
const simularLocalmente = (valorBem, tipo, plano) => {
  const condicoes = tipo === 'maior_parcela' ? condicoesHardcoded.menor_entrada : condicoesHardcoded.padrao;
  
  // Filtrar por plano
  let condicoesFiltradas = condicoes;
  if (plano === 'Semestral') {
    condicoesFiltradas = condicoes.filter(c => c.plano && c.plano.includes('Semestral'));
  } else if (plano === 'Anual') {
    condicoesFiltradas = condicoes.filter(c => c.plano && c.plano.includes('Anual'));
  }
  
  const resultados = [];
  
  for (const banco of condicoesFiltradas) {
    let lance_sugerido = banco.lance_sugerido;
    let taxa = banco.taxa;
    let embutido = banco.embutido;
    let prazo = banco.prazo;
    
    // Definir multiplicador do plano
    let mult = 1;
    if (plano === 'Semestral') mult = 6;
    else if (plano === 'Anual') mult = 12;
    
    let valor_embutido, valor_cota, taxas, cota_taxas, credito_liquido;
    let perc_recurso_proprio_lance, valor_recurso_proprio_lance, saldo_devedor;
    let parcela_antes, parcela_pos, prazo_final, parcelas_pagas, valor_recurso_proprio;
    let perc_recurso_proprio;
    
    if (tipo === 'maior_parcela') {
      credito_liquido = valorBem;
      // Fórmula correta considerando taxa e embutido
      if (banco.banco === 'Brasil' || banco.banco === 'Santander' || banco.banco === 'Caixa') {
        let fator = 1 - (1 + 0) * embutido;
        valor_cota = fator !== 0 ? credito_liquido / fator : 0;
        taxas = taxa * valor_cota;
      } else {
        let fator = 1 - (1 + taxa) * embutido;
        valor_cota = fator !== 0 ? credito_liquido / fator : 0;
        taxas = taxa * valor_cota;
      }
      
      cota_taxas = valor_cota + taxas;
      
      if (banco.banco === 'Brasil' || banco.banco === 'Santander' || banco.banco === 'Caixa') {
        valor_embutido = valor_cota * embutido;
      } else {
        valor_embutido = cota_taxas * embutido;
      }
      
      perc_recurso_proprio_lance = lance_sugerido - embutido;
      
      if (tipo === 'menor_parcela' && (banco.banco === 'Bradesco' || banco.banco === 'Santander')) {
        // Opção Padrão - alterações específicas para Bradesco e Santander
        valor_recurso_proprio_lance = perc_recurso_proprio_lance * credito_liquido;
        saldo_devedor = cota_taxas - valor_embutido - valor_recurso_proprio_lance;
        parcela_antes = (cota_taxas / prazo) * mult;
        prazo_final = 52;
        parcela_pos = (saldo_devedor / prazo_final) * mult;
        let parcela_antes_mensal = cota_taxas / prazo;
        let parcela_pos_mensal = saldo_devedor / prazo; // Usar prazo em vez de prazo_final
        parcelas_pagas = (parcela_antes_mensal * 2) + (parcela_pos_mensal * 2);
        valor_recurso_proprio = valor_recurso_proprio_lance; // Não somar parcelas_pagas
      } else if (banco.banco === 'Bradesco' || banco.banco === 'Santander') {
        // Opção Menor Entrada - manter lógica original
        valor_recurso_proprio_lance = perc_recurso_proprio_lance * cota_taxas;
        saldo_devedor = cota_taxas - valor_embutido - valor_recurso_proprio_lance;
        parcela_antes = (cota_taxas / prazo) * mult;
        prazo_final = 52;
        parcela_pos = (saldo_devedor / prazo) * mult; // Usar prazo em vez de prazo_final
        let parcela_antes_mensal = cota_taxas / prazo;
        let parcela_pos_mensal = saldo_devedor / prazo; // Usar prazo em vez de prazo_final
        parcelas_pagas = (parcela_antes_mensal * 2) + (parcela_pos_mensal * 2);
        valor_recurso_proprio = valor_recurso_proprio_lance; // Usar apenas o valor do lance sugerido
      } else if (banco.banco === 'Brasil' || banco.banco === 'Caixa') {
        // Brasil e Caixa - usar valor_cota
        valor_recurso_proprio_lance = perc_recurso_proprio_lance * valor_cota;
        saldo_devedor = cota_taxas - valor_embutido - valor_recurso_proprio_lance;
        parcela_antes = (cota_taxas / prazo) * mult;
        prazo_final = null;
        parcela_pos = (saldo_devedor / prazo) * mult;
        parcelas_pagas = 0;
        valor_recurso_proprio = valor_recurso_proprio_lance;
      } else {
        // Outros bancos - manter lógica original
        valor_recurso_proprio_lance = perc_recurso_proprio_lance * cota_taxas;
        saldo_devedor = cota_taxas - valor_embutido - valor_recurso_proprio_lance;
        parcela_antes = (cota_taxas / prazo) * mult;
        prazo_final = null;
        parcela_pos = (saldo_devedor / prazo) * mult;
        parcelas_pagas = 0;
        valor_recurso_proprio = valor_recurso_proprio_lance;
        
      }
      
      perc_recurso_proprio = valor_cota ? valor_recurso_proprio / valor_cota : 0;
    } else {
      valor_embutido = 0;
      valor_cota = valorBem;
      credito_liquido = valorBem; // Definir credito_liquido para opção Padrão
      taxas = taxa * valor_cota;
      cota_taxas = valor_cota + taxas;
      perc_recurso_proprio_lance = lance_sugerido;
      
      if (banco.banco === 'Bradesco' || banco.banco === 'Santander' || banco.banco === 'Brasil' || banco.banco === 'Caixa') {
        // Bradesco, Santander, Brasil e Caixa - usar credito_liquido
        valor_recurso_proprio_lance = perc_recurso_proprio_lance * credito_liquido;
      } else {
        // Demais bancos - usar cota_taxas
        valor_recurso_proprio_lance = perc_recurso_proprio_lance * cota_taxas;
      }
      
      saldo_devedor = cota_taxas - valor_recurso_proprio_lance;
      parcela_antes = (cota_taxas / prazo) * mult;
      
      if (banco.banco === 'Bradesco' || banco.banco === 'Santander') {
        prazo_final = 52;
        parcela_pos = (saldo_devedor / prazo) * mult; // Usar prazo em vez de prazo_final
        let parcela_antes_mensal = cota_taxas / prazo;
        let parcela_pos_mensal = saldo_devedor / prazo; // Usar prazo em vez de prazo_final
        parcelas_pagas = (parcela_antes_mensal * 2) + (parcela_pos_mensal * 2);
        valor_recurso_proprio = valor_recurso_proprio_lance; // Usar apenas o valor do lance sugerido
      } else {
        prazo_final = null;
        parcela_pos = (saldo_devedor / prazo) * mult;
        parcelas_pagas = 0;
        valor_recurso_proprio = valor_recurso_proprio_lance;
      }
      
      perc_recurso_proprio = valor_cota ? valor_recurso_proprio / valor_cota : 0;
    }
    
    // Calcular propriedades adicionais para o filtro analítico
    let valor_financiado = credito_liquido - valor_recurso_proprio;
    let periodo;
    if (banco.banco === 'Bradesco' || banco.banco === 'Santander') {
      periodo = prazo_final;
    } else {
      periodo = prazo;
    }
    let saldo_devedor_result = saldo_devedor;
    let parcela_utilizada = parcela_pos;
    
    // Calcular taxas conforme backend
    let taxa_mensal, taxa_anual;
    try {
      taxa_mensal = (saldo_devedor / valor_financiado) ** (1 / periodo) - 1;
      taxa_anual = (1 + taxa_mensal) ** 12 - 1;
    } catch (error) {
      taxa_mensal = 0;
      taxa_anual = 0;
    }
    
    // Arredondar todos os valores para 2 casas decimais
    valor_cota = Math.round(valor_cota * 100) / 100;
    taxas = Math.round(taxas * 100) / 100;
    cota_taxas = Math.round(cota_taxas * 100) / 100;
    credito_liquido = Math.round(credito_liquido * 100) / 100;
    valor_embutido = Math.round(valor_embutido * 100) / 100;
    perc_recurso_proprio_lance = Math.round(perc_recurso_proprio_lance * 100) / 100;
    valor_recurso_proprio_lance = Math.round(valor_recurso_proprio_lance * 100) / 100;
    saldo_devedor = Math.round(saldo_devedor * 100) / 100;
    parcela_antes = Math.round(parcela_antes * 100) / 100;
    parcela_pos = Math.round(parcela_pos * 100) / 100;
    parcelas_pagas = Math.round(parcelas_pagas * 100) / 100;
    valor_recurso_proprio = Math.round(valor_recurso_proprio * 100) / 100;
    perc_recurso_proprio = Math.round(perc_recurso_proprio * 100) / 100;
    valor_financiado = Math.round(valor_financiado * 100) / 100;
    saldo_devedor_result = Math.round(saldo_devedor_result * 100) / 100;
    parcela_utilizada = Math.round(parcela_utilizada * 100) / 100;
    taxa_mensal = Math.round(taxa_mensal * 10000) / 10000; // 4 casas para taxa
    taxa_anual = Math.round(taxa_anual * 100) / 100;
    
    resultados.push({
      banco: banco.banco,
      prazo: banco.prazo,
      prazo_final: prazo_final,
      taxa: taxa,
      embutido: embutido,
      lance_sugerido: lance_sugerido,
      plano: banco.plano,
      atende: banco.atende,
      possui_lance_embutido: banco.possui_lance_embutido,
      carta_contemplada: banco.carta_contemplada,
      valor_cota: valor_cota,
      taxas: taxas,
      cota_taxas: cota_taxas,
      credito_liquido: credito_liquido,
      valor_embutido: valor_embutido,
      perc_recurso_proprio_lance: perc_recurso_proprio_lance,
      valor_recurso_proprio_lance: valor_recurso_proprio_lance,
      saldo_devedor: saldo_devedor,
      parcela_antes: parcela_antes,
      parcela_pos: parcela_pos,
      parcelas_pagas: parcelas_pagas,
      valor_recurso_proprio: valor_recurso_proprio,
      perc_recurso_proprio: perc_recurso_proprio,
      // Adicionar propriedades que o frontend espera
      parcela_antes_contemplacao: parcela_antes,
      parcela_pos_contemplacao: parcela_pos,
      // Propriedades para o filtro analítico
      valor_financiado: valor_financiado,
      periodo: periodo,
      saldo_devedor_result: saldo_devedor_result,
      parcela_utilizada: parcela_utilizada,
      taxa_mensal: taxa_mensal,
      taxa_anual: taxa_anual,
      credito_liquido: credito_liquido
    });
  }
  
  return resultados;
};

function App() {
  const [step, setStep] = useState(1); // 1: valor, 2: filtros/resultados
  const [valorBem, setValorBem] = useState('');
  const [valorBemRaw, setValorBemRaw] = useState('');
  const [resultados, setResultados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tipo, setTipo] = useState('menor_parcela');
  const [planoSelecionado, setPlanoSelecionado] = useState('Mensal');
  const [bancosDisponiveis, setBancosDisponiveis] = useState(bancosFixos);
  const [bancosSelecionados, setBancosSelecionados] = useState([]);
  const [tabelaCondicoes, setTabelaCondicoes] = useState([]);

  const [showBancosDropdown, setShowBancosDropdown] = useState(false);
  const [erroFiltro, setErroFiltro] = useState('');
  const dropdownRef = useRef(null);
  const [barter, setBarter] = useState(false);
  const [cpr, setCpr] = useState(false);
  const [shouldSimulate, setShouldSimulate] = useState(false);
  const [valorExibido, setValorExibido] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [resultadosOriginais, setResultadosOriginais] = useState([]);
  const [cotacoes, setCotacoes] = useState({ soja: null, milho: null });
  const [gerandoPDF, setGerandoPDF] = useState(false);
  const [informarPercentualEntrada, setInformarPercentualEntrada] = useState(false);
  const [percentualEntrada, setPercentualEntrada] = useState('');
  const [informarPercentualEntradaCPR, setInformarPercentualEntradaCPR] = useState(false);
  const [percentualEntradaCPR, setPercentualEntradaCPR] = useState('');
  
  // Estados para as novas operações - Valor do Pedido
  const [barterValorPedido, setBarterValorPedido] = useState(false);
  const [informarPercentualEntradaBarterPedido, setInformarPercentualEntradaBarterPedido] = useState(false);
  const [percentualEntradaBarterPedido, setPercentualEntradaBarterPedido] = useState('');
  const [cprValorPedido, setCprValorPedido] = useState(false);
  const [informarPercentualEntradaCPRPedido, setInformarPercentualEntradaCPRPedido] = useState(false);
  const [percentualEntradaCPRPedido, setPercentualEntradaCPRPedido] = useState('');
  const [mostrarOperacoesPedido, setMostrarOperacoesPedido] = useState(false);

  // Definir ordem fixa dos bancos
  const ordemBancos = ['Bradesco', 'Santander', 'Brasil', 'Caixa', 'Sicoob'];

  // Responsividade dinâmica para filtros
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 700);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 700);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Função para buscar cotações mais recentes
  const buscarCotacoes = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/cotacoes-recentes`);
      setCotacoes(response.data);
    } catch (error) {
      console.error('Erro ao buscar cotações:', error);
      setCotacoes({
        soja: { valor: null, data_hora: null, texto_original: 'Erro ao buscar' },
        milho: { valor: null, data_hora: null, texto_original: 'Erro ao buscar' }
      });
    }
  };

  // Carregar cotações ao iniciar o app
  useEffect(() => {
    buscarCotacoes();
  }, []);

  // Carregar condições para checklist de bancos e filtro de planos
  useEffect(() => {
    if (step === 2) {
      // Usar condições hardcoded em vez da API
      let dados = simularLocalmente(0, tipo, planoSelecionado || 'Mensal');
      let condicoes = dados.map(r => ({
          banco: r.banco,
          plano: r.plano,
          atende: r.atende,
          possui_lance_embutido: r.possui_lance_embutido,
          carta_contemplada: r.carta_contemplada,
          embutido: r.embutido
        }));
        // Se for Menor Entrada, filtrar apenas bancos que possuem lance embutido
        if (tipo === 'maior_parcela') {
          condicoes = condicoes.filter(c => c.possui_lance_embutido === 'Sim');
        }
        condicoes.sort((a, b) => ordemBancos.indexOf(a.banco) - ordemBancos.indexOf(b.banco));
        setTabelaCondicoes(condicoes);
        setBancosDisponiveis(
          ordemBancos
            .map(nome => bancosFixos.find(b => b.nome === nome))
            .filter(b => b && condicoes.map(c => c.banco).includes(b.nome))
        );
    }
    // Carregar tabela de condições para Tela 1 (sempre Mensal/Padrão)
    if (step === 1) {
      // Usar condições hardcoded em vez da API
      let dados = simularLocalmente(0, 'menor_parcela', 'Mensal');
      let condicoes = dados.map(r => ({
          banco: r.banco,
          plano: r.plano,
          atende: r.atende,
          possui_lance_embutido: r.possui_lance_embutido,
          carta_contemplada: r.carta_contemplada
        }));
        condicoes.sort((a, b) => ordemBancos.indexOf(a.banco) - ordemBancos.indexOf(b.banco));
        setTabelaCondicoes(condicoes);
    }
  }, [step, tipo, planoSelecionado, ordemBancos]);

  // Sempre que mudar plano ou tipo de cálculo na Tela 2, limpe os resultados e valide bancos selecionados
  useEffect(() => {
    if (step === 2) {
      setResultados([]);
      
      // Limpar erro quando mudar para Padrão
      if (tipo === 'menor_parcela') {
        setErroFiltro('');
      }
      
      // Validação: se trocar para Menor Entrada, verificar bancos selecionados
      if (tipo === 'maior_parcela' && bancosSelecionados.length > 0) {
        const bancosInvalidos = bancosSelecionados.filter(banco => {
          const cond = tabelaCondicoes.find(c => c.banco === banco);
          return !cond || cond.possui_lance_embutido !== 'Sim';
        });
        if (bancosInvalidos.length > 0) {
          setErroFiltro('Esse banco não possui lance embutido, filtre novamente.');
          setBancosSelecionados([]);
        }
      }
      
      // Atualizar automaticamente quando mudar tipo ou plano
      if (valorBem && parseFloat(valorBem) > 0) {
        setShouldSimulate(true);
      }
    }
  }, [planoSelecionado, tipo, step, tabelaCondicoes, valorBem, bancosSelecionados]);

  // Sempre que step mudar, resetar barter e cpr
  useEffect(() => {
    setBarter(false);
    setInformarPercentualEntrada(false);
    setPercentualEntrada('');
    setCpr(false);
    setInformarPercentualEntradaCPR(false);
    setPercentualEntradaCPR('');
    setBarterValorPedido(false);
    setInformarPercentualEntradaBarterPedido(false);
    setPercentualEntradaBarterPedido('');
    setCprValorPedido(false);
    setInformarPercentualEntradaCPRPedido(false);
    setPercentualEntradaCPRPedido('');
    setMostrarOperacoesPedido(false);
  }, [step]);

  // Atualizar automaticamente quando mudar formato (S/A)
  useEffect(() => {
    if (step === 2 && valorBem && parseFloat(valorBem) > 0 && resultados.length > 0) {
      // O formato não afeta os dados, apenas a visualização
      // Não precisa fazer nova requisição, apenas garantir que os resultados estão atualizados
    }
    }, [step, valorBem, resultados]);

  // Filtrar resultados existentes quando mudar bancos selecionados
  useEffect(() => {
    if (step === 2 && resultadosOriginais.length > 0 && !shouldSimulate) {
      let dadosFiltrados = [...resultadosOriginais];
      
      // Filtrar por bancos selecionados
      if (bancosSelecionados.length > 0) {
        dadosFiltrados = dadosFiltrados.filter(r => bancosSelecionados.includes(r.banco));
      }
      
      setResultados(dadosFiltrados);
    }
  }, [bancosSelecionados, step, resultadosOriginais, shouldSimulate]);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowBancosDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleValorChange = (e) => {
    let raw = e.target.value.replace(/\D/g, '');
    if (!raw) {
      setValorBem('');
      setValorBemRaw('');
      return;
    }
    let valor = (parseInt(raw, 10) / 100).toFixed(2);
    setValorBem(valor);
    setValorBemRaw(raw);
  };

  const handlePercentualEntradaChange = (e) => {
    let valor = e.target.value.replace(/[^\d,]/g, '').replace(',', '.');
    valor = formatPercentual(valor);
    setPercentualEntrada(valor);
  };

  const handlePercentualEntradaCPRChange = (e) => {
    let valor = e.target.value.replace(/[^\d,]/g, '').replace(',', '.');
    valor = formatPercentual(valor);
    setPercentualEntradaCPR(valor);
  };

  // Handlers para as novas operações - Valor do Pedido
  const handlePercentualEntradaBarterPedidoChange = (e) => {
    let valor = e.target.value.replace(/[^\d,]/g, '').replace(',', '.');
    valor = formatPercentual(valor);
    setPercentualEntradaBarterPedido(valor);
  };

  const handlePercentualEntradaCPRPedidoChange = (e) => {
    let valor = e.target.value.replace(/[^\d,]/g, '').replace(',', '.');
    valor = formatPercentual(valor);
    setPercentualEntradaCPRPedido(valor);
  };



  const handleSimularClick = async () => {
    // Registrar simulação no Google Sheets
    await registrarSimulacao(parseFloat(valorBem || 0));
    
    setShowModal(true);
    // Executar simulação imediatamente
    setErroFiltro('');
    setTipo('menor_parcela');
    setPlanoSelecionado('Mensal');
    setStep(2);
    setShouldSimulate(true);
    setValorExibido(valorBem);
    // Fechar pop-up após 20 segundos
    setTimeout(() => {
      setShowModal(false);
    }, 20000);
  };

  // Atualização automática dos resultados ao mudar filtros
  useEffect(() => {
    if (step === 2 && valorBem && parseFloat(valorBem) > 0 && shouldSimulate) {
      setLoading(true);
      
      try {
        // Usar simulação local em vez da API
        let dados = simularLocalmente(parseFloat(valorBem), tipo, planoSelecionado || 'Mensal');
          
          if (!dados || dados.length === 0) {
            setResultados([]);
            setValorExibido(valorBem);
            return;
          }
          
          // Filtrar por bancos selecionados
          if (bancosSelecionados.length > 0) {
            dados = dados.filter(r => bancosSelecionados.includes(r.banco));
          }
          
          // Filtrar por tipo de simulação
          if (tipo === 'maior_parcela') {
            dados = dados.filter(r => r.possui_lance_embutido === 'Sim');
          }
          
          setResultados(dados);
          setResultadosOriginais(dados); // Salvar resultados originais
          setValorExibido(valorBem);
      } catch (error) {
        console.error('Erro na simulação local:', error);
        alert('Erro ao simular. Verifique os dados.');
      } finally {
          setLoading(false);
          setShouldSimulate(false);
      }
    }
  }, [valorBem, tipo, planoSelecionado, bancosSelecionados, bancosDisponiveis, step, shouldSimulate]);

  const getBancoUrl = (nome) => {
    const banco = bancosFixos.find(b => b.nome === nome);
    return banco ? banco.url : '';
  };

  // Função para gerar PDF
  const gerarPDF = async () => {
    if (resultados.length === 0) {
      alert('Não há resultados para gerar PDF');
      return;
    }

    setGerandoPDF(true);
    
    try {
      // Criar um elemento temporário para o PDF
      const pdfContent = document.createElement('div');
      pdfContent.style.padding = '20px';
      pdfContent.style.fontFamily = 'Arial, sans-serif';
      pdfContent.style.fontSize = '12px';
      pdfContent.style.color = '#000';
      pdfContent.style.backgroundColor = '#fff';
      pdfContent.style.width = '800px';
      
      // Cabeçalho
      pdfContent.innerHTML = `
        <div style="margin-bottom: 20px; border-bottom: 2px solid #E31313; padding-bottom: 10px;">
          <div style="text-align: center;">
            <div style="display: flex; align-items: center; justify-content: center; gap: 12px; margin-bottom: 10px;">
              <img src="https://i.imgur.com/RcqpwUP.png" alt="Grafismo" style="width: 60px; height: auto;" />
              <h1 style="color: #1e293b; margin: 0; font-size: 24px; font-weight: 700; letter-spacing: 1px;">Painel de Crédito</h1>
            </div>
            <p style="margin: 5px 0; color: #666;">Data: ${new Date().toLocaleDateString('pt-BR')}</p>
            <p style="margin: 5px 0; color: #666;">Valor do Bem: R$ ${Number(valorExibido).toLocaleString('pt-BR', {minimumFractionDigits: 2})}</p>
            <p style="margin: 5px 0; color: #666;">Tipo: ${tipo === 'menor_parcela' ? 'Padrão' : 'Menor Entrada'}</p>
            <p style="margin: 5px 0; color: #666;">Plano: ${planoSelecionado}</p>
          </div>
        </div>
      `;

      // Tabela de resultados
      const tabelaHTML = `
        <table style="width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 11px;">
          <thead>
            <tr style="background-color: #F7F7F7;">
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Banco</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Valor do Pedido</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Prazo</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Parcelas</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Valor Recurso Próprio</th>
            </tr>
          </thead>
          <tbody>
            ${resultados
              .slice()
              .sort((a, b) => ordemBancos.indexOf(a.banco) - ordemBancos.indexOf(b.banco))
              .map((r, i) => `
                <tr style="border-bottom: 1px solid #eee;">
                  <td style="border: 1px solid #ddd; padding: 8px;">${r.banco}</td>
                  <td style="border: 1px solid #ddd; padding: 8px;">R$ ${(Number(valorExibido) || 0).toLocaleString('pt-BR', {minimumFractionDigits: 2})}</td>
                  <td style="border: 1px solid #ddd; padding: 8px;">${formatarPrazo(r.prazo, planoSelecionado)}</td>
                  <td style="border: 1px solid #ddd; padding: 8px;">R$ ${(r.parcela_pos_contemplacao || 0).toLocaleString('pt-BR', {minimumFractionDigits: 2})}</td>
                  <td style="border: 1px solid #ddd; padding: 8px;">R$ ${(r.valor_recurso_proprio || 0).toLocaleString('pt-BR', {minimumFractionDigits: 2})}</td>
                </tr>
              `).join('')}
          </tbody>
        </table>
      `;
      
      pdfContent.innerHTML += tabelaHTML;

      // Informações adicionais
      if (barter) {
        pdfContent.innerHTML += `
          <div style="margin-top: 20px; padding: 15px; background-color: #F7F7F7; border-radius: 8px;">
            <h3 style="color: #E31313; margin: 0 0 10px 0; font-size: 16px;">Recurso Próprio em Barter:</h3>
            ${resultados
              .slice()
              .sort((a, b) => ordemBancos.indexOf(a.banco) - ordemBancos.indexOf(b.banco))
              .map((r, i) => {
                const valorRecursoProprioAjustado = calcularValorRecursoProprioAjustado(
                  r.valor_recurso_proprio,
                  parseFloat(valorExibido || valorBem || 0),
                  percentualEntrada,
                  informarPercentualEntrada
                );
                const valoresCorrigidos = calcularValoresCorrigidos(valorRecursoProprioAjustado);
                
                return `
                  <div style="margin-bottom: 15px; padding: 10px; background-color: #fff; border-radius: 5px;">
                    <div style="font-weight: bold; color: #E31313; margin-bottom: 8px; font-size: 14px;">${r.banco}</div>
                    
                    ${informarPercentualEntrada && percentualEntrada ? `
                      <!-- Informações de entrada percentual -->
                      <div style="background-color: #E8F5E8; padding: 8px; border-radius: 4px; border: 1px solid #4CAF50; margin-bottom: 10px;">
                        <div style="font-size: 11px; color: #2E7D32; font-weight: 500; margin-bottom: 4px;">
                          💰 Informações de Entrada:
                        </div>
                        <div style="font-size: 10px; color: #388E3C;">
                          • Percentual de entrada: <strong>${percentualEntrada}%</strong><br>
                          • Valor da entrada: <strong>R$ ${((parseFloat(valorExibido || valorBem || 0) * parseFloat(percentualEntrada)) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong><br>
                          • Valor ajustado para Barter: <strong>R$ ${valorRecursoProprioAjustado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong>
                        </div>
                      </div>
                    ` : ''}
                    
                    <!-- Valores iniciais -->
                    <div style="color: #333; font-size: 10px; margin-bottom: 10px;">
                      Sacas Disponíveis/Soja: <strong>${formatSacas(cotacoes.soja?.valor ? valorRecursoProprioAjustado / cotacoes.soja.valor : null)}</strong><br>
                      Sacas Disponíveis/Milho: <strong>${formatSacas(cotacoes.milho?.valor ? valorRecursoProprioAjustado / cotacoes.milho.valor : null)}</strong>
                    </div>
                    
                    <!-- Tabela de valores corrigidos -->
                    <div style="margin-top: 10px;">
                      <div style="font-weight: bold; color: #333; margin-bottom: 5px; font-size: 10px;">
                        Correção por Prazo de Pagamento:
                      </div>
                      <table style="width: 100%; border-collapse: collapse; font-size: 8px;">
                        <thead>
                          <tr style="background-color: #666666; color: #fff;">
                            <th style="padding: 3px 2px; text-align: center; border: 1px solid #ccc;">Dias</th>
                            ${valoresCorrigidos.map((v, idx) => `
                              <th style="padding: 3px 2px; text-align: center; border: 1px solid #ccc; min-width: 30px;">
                                ${v.mes}
                              </th>
                            `).join('')}
                          </tr>
                        </thead>
                        <tbody>
                          <tr style="background-color: #fff;">
                            <td style="padding: 3px 2px; text-align: center; border: 1px solid #ccc; font-weight: bold; font-size: 8px;">
                              Soja
                            </td>
                            ${valoresCorrigidos.map((v, idx) => `
                              <td style="padding: 3px 2px; text-align: center; border: 1px solid #ccc; font-size: 8px;">
                                ${formatSacas(cotacoes.soja?.valor ? v.valorCorrigido / cotacoes.soja.valor : null)}
                              </td>
                            `).join('')}
                          </tr>
                          <tr style="background-color: #f9f9f9;">
                            <td style="padding: 3px 2px; text-align: center; border: 1px solid #ccc; font-weight: bold; font-size: 8px;">
                              Milho
                            </td>
                            ${valoresCorrigidos.map((v, idx) => `
                              <td style="padding: 3px 2px; text-align: center; border: 1px solid #ccc; font-size: 8px;">
                                ${formatSacas(cotacoes.milho?.valor ? v.valorCorrigido / cotacoes.milho.valor : null)}
                              </td>
                            `).join('')}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                `;
              }).join('')}
          </div>
        `;
      }

      // Informações adicionais - Operação CPR
      if (cpr) {
        pdfContent.innerHTML += `
          <div style="margin-top: 20px; padding: 15px; background-color: #F7F7F7; border-radius: 8px;">
            <h3 style="color: #E31313; margin: 0 0 10px 0; font-size: 16px;">Operação CPR:</h3>
            ${resultados
              .slice()
              .sort((a, b) => ordemBancos.indexOf(a.banco) - ordemBancos.indexOf(b.banco))
              .map((r, i) => {
                const valorRecursoProprioAjustadoCPR = calcularValorRecursoProprioAjustado(r.valor_recurso_proprio, valorExibido || valorBem || 0, percentualEntradaCPR, informarPercentualEntradaCPR);
                const valoresCPR = calcularOperacaoCPR(valorRecursoProprioAjustadoCPR);
                // Filtrar apenas os meses que têm amortização
                const mesesComAmortizacao = valoresCPR.filter(v => v.amortizacao > 0);
                
                let entradaInfo = '';
                if (informarPercentualEntradaCPR && percentualEntradaCPR && !isNaN(parseFloat(percentualEntradaCPR))) {
                  const valorEntrada = parseFloat(percentualEntradaCPR) / 100 * (valorExibido || valorBem || 0);
                  entradaInfo = `
                    <div style="margin-bottom: 10px; padding: 8px; background-color: #E8F5E8; border-radius: 4px; border: 1px solid #4CAF50;">
                      <div style="font-size: 10px; color: #2E7D32; font-weight: 500; margin-bottom: 3px;">
                        💰 Entrada em dinheiro: ${percentualEntradaCPR}% = R$ ${valorEntrada.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </div>
                      <div style="font-size: 9px; color: #388E3C; margin-top: 2px;">
                        Valor para CPR: R$ ${valorRecursoProprioAjustadoCPR.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </div>
                    </div>
                  `;
                }
                
                return `
                  <div style="margin-bottom: 15px; padding: 10px; background-color: #fff; border-radius: 5px;">
                    <div style="font-weight: bold; color: #E31313; margin-bottom: 8px; font-size: 14px;">${r.banco}</div>
                    
                    ${entradaInfo}
                    
                    <div style="font-weight: bold; color: #333; margin-bottom: 5px; font-size: 10px;">
                      Cálculo CPR - Valor Financiado: R$ ${formatMilhar(valorRecursoProprioAjustadoCPR)}
                    </div>
                    
                    <table style="width: auto; border-collapse: collapse; font-size: 8px;">
                      <thead>
                        <tr style="background-color: #666666; color: #fff;">
                          <th style="padding: 3px 8px; text-align: center; border: 1px solid #ccc; white-space: nowrap;">Data</th>
                          <th style="padding: 3px 8px; text-align: center; border: 1px solid #ccc; white-space: nowrap;">Amortização</th>
                        </tr>
                      </thead>
                      <tbody>
                        ${mesesComAmortizacao.map((v, idx) => `
                          <tr style="background-color: ${idx % 2 === 0 ? '#fff' : '#f9f9f9'};">
                            <td style="padding: 3px 8px; text-align: center; border: 1px solid #ccc; font-size: 8px; white-space: nowrap;">
                              ${v.data.toLocaleDateString('pt-BR')}
                            </td>
                            <td style="padding: 3px 8px; text-align: right; border: 1px solid #ccc; font-size: 8px; white-space: nowrap;">
                              R$ ${formatMilhar(v.amortizacao)}
                            </td>
                          </tr>
                        `).join('')}
                      </tbody>
                    </table>
                  </div>
                `;
              }).join('')}
          </div>
        `;
      }

      // Informações adicionais - Operação Barter - Valor do Pedido
      if (barterValorPedido) {
        const valorDigitadoAjustado = calcularValorRecursoProprioAjustado(parseFloat(valorExibido || valorBem || 0), parseFloat(valorExibido || valorBem || 0), percentualEntradaBarterPedido, informarPercentualEntradaBarterPedido);
        const valoresCorrigidos = calcularValoresCorrigidos(valorDigitadoAjustado);
        
        let entradaInfo = '';
        if (informarPercentualEntradaBarterPedido && percentualEntradaBarterPedido && !isNaN(parseFloat(percentualEntradaBarterPedido))) {
          const valorEntrada = parseFloat(percentualEntradaBarterPedido) / 100 * (valorExibido || valorBem || 0);
          entradaInfo = `
            <div style="margin-bottom: 10px; padding: 8px; background-color: #E8F5E8; border-radius: 4px; border: 1px solid #4CAF50;">
              <div style="font-size: 10px; color: #2E7D32; font-weight: 500; margin-bottom: 3px;">
                💰 Entrada em dinheiro: ${percentualEntradaBarterPedido}% = R$ ${valorEntrada.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </div>
              <div style="font-size: 9px; color: #388E3C; margin-top: 2px;">
                Valor para Barter: R$ ${valorDigitadoAjustado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </div>
            </div>
          `;
        }
        
        pdfContent.innerHTML += `
          <div style="margin-top: 20px; padding: 15px; background-color: #F7F7F7; border-radius: 8px;">
            <h3 style="color: #E31313; margin: 0 0 10px 0; font-size: 16px;">Valor do Pedido em Barter:</h3>
            <div style="margin-bottom: 15px; padding: 10px; background-color: #fff; border-radius: 5px;">
              ${entradaInfo}
              
              <div style="font-weight: bold; color: #333; margin-bottom: 5px; font-size: 10px;">
                Correção por Prazo de Pagamento:
              </div>
              
              <table style="width: 100%; border-collapse: collapse; font-size: 8px;">
                <thead>
                  <tr style="background-color: #666666; color: #fff;">
                    <th style="padding: 3px 4px; text-align: center; border: 1px solid #ccc;">Dias</th>
                    ${valoresCorrigidos.map(v => `
                      <th style="padding: 3px 4px; text-align: center; border: 1px solid #ccc; min-width: 40px;">
                        ${v.mes}
                      </th>
                    `).join('')}
                  </tr>
                </thead>
                <tbody>
                  <tr style="background-color: #fff;">
                    <td style="padding: 3px 4px; text-align: center; border: 1px solid #ccc; font-weight: bold; font-size: 8px;">
                      Soja
                    </td>
                    ${valoresCorrigidos.map(v => `
                      <td style="padding: 3px 4px; text-align: center; border: 1px solid #ccc; font-size: 8px;">
                        ${formatSacas(cotacoes.soja?.valor ? v.valorCorrigido / cotacoes.soja.valor : null)}
                      </td>
                    `).join('')}
                  </tr>
                  <tr style="background-color: #f9f9f9;">
                    <td style="padding: 3px 4px; text-align: center; border: 1px solid #ccc; font-weight: bold; font-size: 8px;">
                      Milho
                    </td>
                    ${valoresCorrigidos.map(v => `
                      <td style="padding: 3px 4px; text-align: center; border: 1px solid #ccc; font-size: 8px;">
                        ${formatSacas(cotacoes.milho?.valor ? v.valorCorrigido / cotacoes.milho.valor : null)}
                      </td>
                    `).join('')}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        `;
      }

      // Informações adicionais - Operação CPR - Valor do Pedido
      if (cprValorPedido) {
        const valorDigitadoAjustadoCPR = calcularValorRecursoProprioAjustado(parseFloat(valorExibido || valorBem || 0), parseFloat(valorExibido || valorBem || 0), percentualEntradaCPRPedido, informarPercentualEntradaCPRPedido);
        const valoresCPR = calcularOperacaoCPR(valorDigitadoAjustadoCPR);
        // Filtrar apenas os meses que têm amortização
        const mesesComAmortizacao = valoresCPR.filter(v => v.amortizacao > 0);
        
        let entradaInfo = '';
        if (informarPercentualEntradaCPRPedido && percentualEntradaCPRPedido && !isNaN(parseFloat(percentualEntradaCPRPedido))) {
          const valorEntrada = parseFloat(percentualEntradaCPRPedido) / 100 * (valorExibido || valorBem || 0);
          entradaInfo = `
            <div style="margin-bottom: 10px; padding: 8px; background-color: #E8F5E8; border-radius: 4px; border: 1px solid #4CAF50;">
              <div style="font-size: 10px; color: #2E7D32; font-weight: 500; margin-bottom: 3px;">
                💰 Entrada em dinheiro: ${percentualEntradaCPRPedido}% = R$ ${valorEntrada.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </div>
              <div style="font-size: 9px; color: #388E3C; margin-top: 2px;">
                Valor para CPR: R$ ${valorDigitadoAjustadoCPR.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </div>
            </div>
          `;
        }
        
        pdfContent.innerHTML += `
          <div style="margin-top: 20px; padding: 15px; background-color: #F7F7F7; border-radius: 8px;">
            <h3 style="color: #E31313; margin: 0 0 10px 0; font-size: 16px;">Operação CPR - Valor do Pedido:</h3>
            <div style="margin-bottom: 15px; padding: 10px; background-color: #fff; border-radius: 5px;">
              ${entradaInfo}
              
              <div style="font-weight: bold; color: #333; margin-bottom: 5px; font-size: 10px;">
                Cálculo CPR - Valor Financiado: R$ ${formatMilhar(valorDigitadoAjustadoCPR)}
              </div>
              
              <table style="width: auto; border-collapse: collapse; font-size: 8px;">
                <thead>
                  <tr style="background-color: #666666; color: #fff;">
                    <th style="padding: 3px 8px; text-align: center; border: 1px solid #ccc; white-space: nowrap;">Data</th>
                    <th style="padding: 3px 8px; text-align: center; border: 1px solid #ccc; white-space: nowrap;">Amortização</th>
                  </tr>
                </thead>
                <tbody>
                  ${mesesComAmortizacao.map((v, idx) => `
                    <tr style="background-color: ${idx % 2 === 0 ? '#fff' : '#f9f9f9'};">
                      <td style="padding: 3px 8px; text-align: center; border: 1px solid #ccc; font-size: 8px; white-space: nowrap;">
                        ${v.data.toLocaleDateString('pt-BR')}
                      </td>
                      <td style="padding: 3px 8px; text-align: right; border: 1px solid #ccc; font-size: 8px; white-space: nowrap;">
                        R$ ${formatMilhar(v.amortizacao)}
                      </td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
          </div>
        `;
      }

      // Rodapé
      pdfContent.innerHTML += `
        <div style="margin-top: 20px; padding-top: 15px; border-top: 1px solid #ddd; font-size: 10px; color: #666;">
          <p style="margin: 5px 0;"><strong>Aviso:</strong> Valores aproximados e sujeitos a alterações até a data da contratação.</p>
          <p style="margin: 5px 0;">Sujeito a análise de crédito e aprovação pela instituição.</p>
          <p style="margin: 5px 0;">Documento gerado em: ${new Date().toLocaleString('pt-BR')}</p>
        </div>
      `;

      // Adicionar ao DOM temporariamente
      document.body.appendChild(pdfContent);

      // Gerar PDF
      const canvas = await html2canvas(pdfContent, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });

      // Remover do DOM
      document.body.removeChild(pdfContent);

      // Criar PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Salvar PDF
      const fileName = `simulacao_consorcio_${new Date().toISOString().slice(0, 10)}.pdf`;
      pdf.save(fileName);

    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      alert('Erro ao gerar PDF. Tente novamente.');
    } finally {
      setGerandoPDF(false);
    }
  };

  // Adicionar estilos CSS
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = modalStyles;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // Layout responsivo e limpo
  return (
    <div style={{
      padding: isMobile ? '20px 15px' : '30px',
      width: '100vw',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#fff',
      // marginTop removido para voltar à posição original
    }}>
      {/* Grafismo ao lado do título */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: isMobile ? 12 : 18, marginBottom: 18, marginTop: 0 }}>
        <img src="https://i.imgur.com/RcqpwUP.png" alt="Grafismo" style={{ width: isMobile ? 60 : 90, display: 'block' }} />
        <h1 style={{ color: '#1e293b', fontWeight: 700, letterSpacing: 1, fontSize: isMobile ? 24 : 32, margin: 0 }}>Painel de Crédito</h1>
      </div>
      {step === 1 && loading ? (
        <div style={{ width: '100vw', height: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ color: '#E31313', fontWeight: 700, fontSize: 20 }}>Aguarde, carregando informações.</span>
        </div>
      ) : step === 2 && loading ? (
        <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <span style={{ color: '#E31313', fontWeight: 700, fontSize: 18 }}>Calculando simulação...</span>
        </div>
      ) : step === 1 && (
        <>
          <div
            className="quadro-condicoes"
            style={{
              background: '#fff',
              borderRadius: 12,
              boxShadow: '0 2px 8px #0001',
              padding: isMobile ? 15 : 20,
              marginBottom: 24,
              width: '100%',
              minWidth: isMobile ? '100%' : 700,
              maxWidth: 1100,
              marginLeft: 'auto',
              marginRight: 'auto',
              display: 'block',
              boxSizing: 'border-box',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
              <label style={{ fontWeight: 600, color: '#334155', marginBottom: 8, display: 'block' }}>Valor do Pedido:</label>
              <input
                type="text"
                placeholder="Digite o valor"
                value={formatMilhar(valorBem)}
                onChange={handleValorChange}
                style={{ width: 250, maxWidth: '100%', padding: '10px', marginBottom: '20px', display: 'block' }}
                inputMode="numeric"
              />
              <button onClick={handleSimularClick} style={{ height: 36, padding: '0 18px', background: '#E31313', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 16, cursor: 'pointer', minWidth: 100 }} disabled={loading || !valorBem}>
                Simular
              </button>
              <div style={{ marginTop: 8, width: '100%' }}>
                <span style={{ 
                  color: '#E31313', 
                  fontWeight: 700, 
                  fontSize: 14,
                  wordWrap: 'break-word',
                  display: 'block',
                  lineHeight: '1.4'
                }}>
                  ** Valores aproximados e sujeitos a alterações até a data da contratação.
                </span>
              </div>
            </div>
            <h2 style={{ color: '#4D4D4D', fontWeight: 700, fontSize: 20, marginBottom: 16, marginTop: 24, textAlign: 'center', width: '100%' }}>Condições dos Bancos</h2>
            <div className="quadro-tabela" style={{ width: '100%', overflowX: 'auto', maxWidth: '100vw' }}>
              <table style={{ borderCollapse: 'collapse', fontSize: 15, tableLayout: 'auto', width: '100%', minWidth: 320 }}>
                <thead>
                  <tr style={{ background: '#F7F7F7' }}>
                    <th style={{ padding: 8, color: '#4D4D4D', fontWeight: 700, textAlign: 'left' }}>Banco</th>
                    <th style={{ padding: 8, color: '#4D4D4D', fontWeight: 700, textAlign: 'left' }}>Plano</th>
                    <th style={{ padding: 8, color: '#4D4D4D', fontWeight: 700, textAlign: 'left' }}>Atende</th>
                    <th style={{ padding: 8, color: '#4D4D4D', fontWeight: 700, textAlign: 'left' }}>Possui Lance Embutido</th>
                    <th style={{ padding: 8, color: '#4D4D4D', fontWeight: 700, textAlign: 'left' }}>Carta Contemplada</th>
                  </tr>
                </thead>
                <tbody>
                  {tabelaCondicoes.map((item, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: 8 }}>
                        <img src={getBancoUrl(item.banco)} alt={item.banco} style={{ height: 36, width: 'auto', maxWidth: 80, objectFit: 'contain', borderRadius: 8, background: '#fff', marginRight: 8, display: 'block' }} />
                      </td>
                      <td style={{ padding: 8 }}>{item.plano}</td>
                      <td style={{ padding: 8 }}>{item.atende}</td>
                      <td style={{ padding: 8 }}>{item.possui_lance_embutido}</td>
                      <td style={{ padding: 8 }}>{item.carta_contemplada}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

        </>
      )}
      {step === 2 && (
        <div
          className="quadro-condicoes quadro-resultados"
          style={{
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 2px 8px #0001',
            padding: isMobile ? 15 : 20,
            marginBottom: 24,
            width: '100%',
            minWidth: isMobile ? '100%' : 700,
            maxWidth: 1100,
            marginLeft: 'auto',
            marginRight: 'auto',
            display: 'block',
            boxSizing: 'border-box',
          }}
        >
          {/* Botões Remover Filtros e Voltar ao Início - Parte Superior */}
          <div
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'center',
              alignItems: 'center',
              gap: isMobile ? 8 : 12,
              marginBottom: 24,
            }}
          >
            <button
              style={{ 
                height: 36, 
                padding: '0 16px', 
                background: '#fff', 
                color: '#1e1e1e', 
                border: '1px solid #ccc', 
                borderRadius: 8, 
                fontWeight: 700, 
                fontSize: 14, 
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                minWidth: isMobile ? '100%' : 140,
                width: isMobile ? '100%' : 'auto'
              }}
              onClick={() => {
                setBancosSelecionados([]);
                setPlanoSelecionado('Mensal');
                setTipo('menor_parcela');
                setBarter(false);
                setInformarPercentualEntrada(false);
                setPercentualEntrada('');
                setBarterValorPedido(false);
                setInformarPercentualEntradaBarterPedido(false);
                setPercentualEntradaBarterPedido('');
                setCprValorPedido(false);
                setInformarPercentualEntradaCPRPedido(false);
                setPercentualEntradaCPRPedido('');
                setMostrarOperacoesPedido(false);
                // Restaurar todos os resultados originais
                if (resultadosOriginais.length > 0) {
                  setResultados([...resultadosOriginais]);
                }
              }}
            >
              Remover Filtros
            </button>
            <button
              style={{ 
                height: 36, 
                padding: '0 16px', 
                background: '#f8f9fa', 
                color: '#6c757d', 
                border: '1px solid #dee2e6', 
                borderRadius: 8, 
                fontWeight: 700, 
                fontSize: 14, 
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                minWidth: isMobile ? '100%' : 140,
                width: isMobile ? '100%' : 'auto'
              }}
              onClick={() => {
                setStep(1);
                setResultados([]);
                setResultadosOriginais([]);
                setValorBem('');
                setValorBemRaw('');
                setValorExibido('');
                setErroFiltro('');
                setBarter(false);
                setInformarPercentualEntrada(false);
                setPercentualEntrada('');
                setBarterValorPedido(false);
                setInformarPercentualEntradaBarterPedido(false);
                setPercentualEntradaBarterPedido('');
                setCprValorPedido(false);
                setInformarPercentualEntradaCPRPedido(false);
                setPercentualEntradaCPRPedido('');
                setMostrarOperacoesPedido(false);
              }}
            >
              Voltar ao Início
            </button>
          </div>
          
          {/* Campo Valor do Pedido e Botões */}
          <div
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? 10 : 12,
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              marginBottom: 24,
            }}
          >
            {/* Campo Valor do Pedido e Botão Atualizar */}
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'flex-start', 
              minWidth: isMobile ? '100%' : 400,
              width: isMobile ? '100%' : 'auto'
            }}>
              <label style={{ fontWeight: 600, color: '#334155', marginBottom: 4, fontSize: 15 }}>Valor do Pedido:</label>
              <div style={{ 
                display: 'flex', 
                flexDirection: isMobile ? 'column' : 'row', 
                alignItems: isMobile ? 'stretch' : 'center', 
                gap: 12,
                width: isMobile ? '100%' : 'auto'
              }}>
                <input
                  type="text"
                  placeholder="Digite o valor"
                  value={formatMilhar(valorBem)}
                  onChange={handleValorChange}
                  style={{ 
                    width: isMobile ? '100%' : 200, 
                    padding: '8px 12px', 
                    border: '1px solid #ccc', 
                    borderRadius: 6, 
                    fontSize: 14
                  }}
                  inputMode="numeric"
                />
                <button
                  style={{ 
                    height: 36, 
                    padding: '0 16px', 
                    background: '#E31313', 
                    color: '#fff', 
                    border: 'none', 
                    borderRadius: 8, 
                    fontWeight: 700, 
                    fontSize: 14, 
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    minWidth: isMobile ? '100%' : 140,
                    width: isMobile ? '100%' : 'auto'
                  }}
                                  onClick={async () => {
                  // Registrar simulação no Google Sheets
                  await registrarSimulacao(parseFloat(valorBem || 0));
                  
                  setShowModal(true);
                  // Executar simulação imediatamente
                  setResultados([]);
                  setErroFiltro('');
                  setBarter(false);
                  setInformarPercentualEntrada(false);
                  setPercentualEntrada('');
                  setBarterValorPedido(false);
                  setInformarPercentualEntradaBarterPedido(false);
                  setPercentualEntradaBarterPedido('');
                  setCprValorPedido(false);
                  setInformarPercentualEntradaCPRPedido(false);
                  setPercentualEntradaCPRPedido('');
                  setShouldSimulate(true);
                  // Fechar pop-up após 20 segundos
                  setTimeout(() => {
                    setShowModal(false);
                  }, 20000);
                }}
                >
                  Atualizar
                </button>
              </div>
              <div style={{ marginTop: 8, width: '100%' }}>
                <span style={{ 
                  color: '#E31313', 
                  fontWeight: 700, 
                  fontSize: 14,
                  wordWrap: 'break-word',
                  display: 'block',
                  lineHeight: '1.4'
                }}>
                  ** Valores aproximados e sujeitos a alterações até a data da contratação.
                </span>
              </div>
            </div>
          </div>
          {/* Filtros em linha */}
          <div
            className="filtros-row"
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? 16 : 8,
              alignItems: isMobile ? 'stretch' : 'flex-end',
            }}
          >
            {/* Dropdown de bancos */}
            <div className="filtro-col banco-col" style={{ width: isMobile ? '100%' : 'auto' }}>
              <span style={{ fontWeight: 600, color: '#334155', marginBottom: 4, fontSize: 15, minHeight: 24 }}>Selecione o Banco:</span>
              <div ref={dropdownRef} style={{ position: 'relative' }}>
                <button
                  onClick={() => setShowBancosDropdown((v) => !v)}
                  style={{
                    height: 36,
                    padding: '0 12px',
                    border: '1px solid #ccc',
                    borderRadius: 6,
                    background: '#fff',
                    color: '#1e1e1e',
                    fontWeight: 500,
                    fontSize: 14,
                    cursor: 'pointer',
                    minWidth: isMobile ? '100%' : 180,
                    width: isMobile ? '100%' : "180px",
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  {bancosSelecionados.length === 0 ? 'Todos os bancos' : `${bancosSelecionados.length} banco(s) selecionado(s)`}
                  <span style={{ fontSize: '12px' }}>▼</span>
                </button>
                {showBancosDropdown && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    width: isMobile ? '100%' : '180px',
                    background: '#fff',
                    border: '1px solid #ccc',
                    borderRadius: 6,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    zIndex: 1000,
                    maxHeight: 200,
                    overflowY: 'auto',
                    marginTop: 2
                  }}>
                    {bancosDisponiveis.length === 0 ? (
                      <div style={{ padding: '12px', color: '#666', textAlign: 'center' }}>
                        Nenhum banco disponível para este filtro
                      </div>
                    ) : (
                      bancosDisponiveis
                        .slice()
                        .sort((a, b) => ordemBancos.indexOf(a.nome) - ordemBancos.indexOf(b.nome))
                        .map(banco => (
                          <label key={banco.nome} style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '8px 12px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f0f0f0',
                            background: bancosSelecionados.includes(banco.nome) ? '#FDECEC' : 'transparent'
                          }}>
                            <input
                              type="checkbox"
                              checked={bancosSelecionados.includes(banco.nome)}
                              onChange={() => {
                                // Validação Menor Entrada
                                if (tipo === 'maior_parcela') {
                                  const bancoCondicao = tabelaCondicoes.find(c => c.banco === banco.nome);
                                  if (bancoCondicao && bancoCondicao.possui_lance_embutido !== 'Sim') {
                                    setErroFiltro('Esse banco não possui lance embutido, filtre novamente.');
                                    return;
                                  }
                                }
                                setErroFiltro('');
                                setBancosSelecionados(prev =>
                                  prev.includes(banco.nome)
                                    ? prev.filter(b => b !== banco.nome)
                                    : [...prev, banco.nome]
                                );
                              }}
                              style={{ accentColor: '#E31313', marginRight: 8 }}
                            />
                            <img src={banco.url} alt={banco.nome} style={{ height: 32, width: 'auto', maxWidth: 70, objectFit: 'contain', marginRight: 8 }} />
                            {/* Removido o nome do banco, exibe só a imagem */}
                          </label>
                        ))
                    )}
                  </div>
                )}
              </div>
            </div>
            {/* Botões tipo de cálculo */}
            <div className="filtro-col tipo-col" style={{ width: isMobile ? '100%' : 'auto' }}>
              <span style={{ fontWeight: 600, color: '#334155', marginBottom: 4, fontSize: 15 }}>Tipo de Simulação:</span>
              <div style={{ 
                display: 'flex', 
                gap: 6,
                flexDirection: isMobile ? 'column' : 'row',
                width: isMobile ? '100%' : 'auto'
              }}>
                <button onClick={() => setTipo('menor_parcela')} style={{ 
                  height: 36, 
                  padding: '0 14px', 
                  borderRadius: 8, 
                  border: tipo === 'menor_parcela' ? '2px solid #E31313' : '1px solid #ccc', 
                  background: tipo === 'menor_parcela' ? '#FDECEC' : '#fff', 
                  color: '#1e1e1e', 
                  fontWeight: 400, 
                  fontSize: 15, 
                  cursor: 'pointer', 
                  minWidth: isMobile ? '100%' : 80,
                  width: isMobile ? '100%' : 'auto'
                }}>Padrão</button>
                <button onClick={() => setTipo('maior_parcela')} style={{ 
                  height: 36, 
                  padding: '0 14px', 
                  borderRadius: 8, 
                  border: tipo === 'maior_parcela' ? '2px solid #E31313' : '1px solid #ccc', 
                  background: tipo === 'maior_parcela' ? '#FDECEC' : '#fff', 
                  color: '#1e1e1e', 
                  fontWeight: 400, 
                  fontSize: 15, 
                  cursor: 'pointer', 
                  minWidth: isMobile ? '100%' : 120,
                  width: isMobile ? '100%' : 'auto'
                }}>Menor Entrada</button>
              </div>
            </div>
            {/* Filtro de Parcelas */}
            <div className="filtro-col" style={{ width: isMobile ? '100%' : 'auto' }}>
              <span style={{ fontWeight: 600, color: '#334155', marginBottom: 4, fontSize: 15 }}>Parcelas:</span>
              <div style={{ 
                display: 'flex', 
                flexDirection: isMobile ? 'column' : 'row', 
                gap: 8,
                width: isMobile ? '100%' : 'auto'
              }}>
                {['Mensal', 'Semestral', 'Anual'].map((plano) => (
                  <button
                    key={plano}
                    onClick={() => setPlanoSelecionado(plano)}
                    style={{
                      height: 36,
                      padding: '0 14px',
                      borderRadius: 8,
                      border: planoSelecionado === plano ? '2px solid #E31313' : '1px solid #ccc',
                      background: planoSelecionado === plano ? '#FDECEC' : '#fff',
                      color: '#1e1e1e',
                      fontWeight: 400,
                      fontSize: 15,
                      cursor: 'pointer',
                      minWidth: isMobile ? '100%' : 80,
                      width: isMobile ? '100%' : 'auto'
                    }}
                  >
                    {plano}
                  </button>
                ))}
              </div>
            </div>

          </div>
          {/* Mensagem de erro */}
          {erroFiltro && (
            <div style={{
              background: '#FEE2E2',
              border: '1px solid #FCA5A5',
              color: '#DC2626',
              padding: '8px 12px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              textAlign: 'center',
              marginBottom: 8
            }}>
              {erroFiltro}
            </div>
          )}
          {/* Botão Simular removido, atualização automática */}
          {/* Resultados */}
      <div style={{ marginTop: '20px' }}>
            {/* O filtro de visualização agora está ao lado das parcelas */}
            {resultados.length > 0 && <h2 style={{ color: '#4D4D4D', fontWeight: 700, fontSize: 20, marginBottom: 16, marginTop: 0, textAlign: 'center', width: '100%' }}>Resultados</h2>}
            {resultados.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#E31313', fontWeight: 600, margin: '32px 0' }}>
                  Nenhum banco disponível para os filtros selecionados.
                </div>
              ) : (
                <div className="quadro-tabela" style={{ overflowX: 'auto', width: '100%', maxWidth: '100vw' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 15, tableLayout: 'auto', marginBottom: 24 }}>
                    <thead>
                      <tr style={{ background: '#F7F7F7' }}>
                        <th style={{ padding: 8, textAlign: 'left', color: '#4D4D4D', fontWeight: 700, minWidth: 90 }}>Banco</th>
                        <th style={{ padding: 8, textAlign: 'left', color: '#4D4D4D', fontWeight: 700 }}>Valor do Pedido</th>
                        <th style={{ padding: 8, textAlign: 'left', color: '#4D4D4D', fontWeight: 700 }}>Prazo</th>
                        <th style={{ padding: 8, textAlign: 'left', color: '#4D4D4D', fontWeight: 700 }}>Parcelas</th>
                        <th style={{ padding: 8, textAlign: 'left', color: '#4D4D4D', fontWeight: 700 }}>Valor Recurso Próprio</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resultados
                        .slice()
                        .sort((a, b) => ordemBancos.indexOf(a.banco) - ordemBancos.indexOf(b.banco))
                        .map((r, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: 8, minWidth: 90 }}>
                            <img src={getBancoUrl(r.banco)} alt={r.banco} style={{ height: 36, width: 'auto', maxWidth: 80, objectFit: 'contain', borderRadius: 8, background: '#fff', marginRight: 8, display: 'block' }} />
                          </td>
                        <td style={{ padding: 8 }}>R$ {(Number(valorExibido) || 0).toLocaleString('pt-BR', {minimumFractionDigits: 2})}</td>
                        <td style={{ padding: 8 }}>{formatarPrazo(r.prazo, planoSelecionado)}</td>
                        <td style={{ padding: 8 }}>R$ {(r.parcela_pos_contemplacao || 0).toLocaleString('pt-BR', {minimumFractionDigits: 2})}</td>
                        <td style={{ padding: 8 }}>R$ {(r.valor_recurso_proprio || 0).toLocaleString('pt-BR', {minimumFractionDigits: 2})}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                
                {/* Botão Gerar PDF */}
                <div style={{ textAlign: 'center', marginTop: 20, paddingTop: 20, borderTop: '1px solid #eee' }}>
                  <button
                    onClick={gerarPDF}
                    disabled={gerandoPDF}
                    style={{
                      height: 36,
                      padding: '0 16px',
                      background: '#E31313',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      fontWeight: 700,
                      fontSize: 14,
                      cursor: gerandoPDF ? 'not-allowed' : 'pointer',
                      whiteSpace: 'nowrap',
                      minWidth: isMobile ? '100%' : 140,
                      width: isMobile ? '100%' : 'auto',
                      opacity: gerandoPDF ? 0.7 : 1,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      transition: 'all 0.2s ease',
                      marginTop: 0
                    }}
                  >
                    {gerandoPDF ? 'Gerando PDF...' : '📄 Gerar PDF'}
                  </button>
                </div>
                
                  {/* Checkbox Operação Barter */}
                  <div style={{ margin: '32px 0 8px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      id="barter"
                      checked={barter}
                      onChange={e => {
                        setBarter(e.target.checked);
                        // Reset percentual quando desmarca barter
                        if (!e.target.checked) {
                          setInformarPercentualEntrada(false);
                          setPercentualEntrada('');
                        }
                      }}
                      style={{ accentColor: '#E31313' }}
                    />
                    <label htmlFor="barter" style={{ fontWeight: 600, color: '#334155', fontSize: 16, cursor: 'pointer' }}>
                      Operação Barter - Valor Recurso Próprio
                    </label>
                  </div>
                  
                  <div style={{ marginBottom: 8, textAlign: 'left' }}>
                    <span style={{ color: '#666', fontSize: 14, fontStyle: 'italic' }}>
                      Sujeito a análise de crédito e aprovação pela instituição.
                    </span>
                  </div>
                  <div style={{ marginBottom: 16, textAlign: 'left' }}>
                    <span style={{ color: '#E31313', fontWeight: 700, fontSize: 14 }}>
                      ** Valores aproximados e sujeitos a alterações até a data da contratação.
                    </span>
                  </div>
                  
                  {/* Checkbox e campo para percentual de entrada - só aparece se Barter estiver marcado */}
                  {barter && (
                    <div style={{ marginLeft: 24, marginBottom: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <input
                          type="checkbox"
                          id="informarPercentualEntrada"
                          checked={informarPercentualEntrada}
                          onChange={e => {
                            setInformarPercentualEntrada(e.target.checked);
                            if (!e.target.checked) {
                              setPercentualEntrada('');
                            }
                          }}
                          style={{ accentColor: '#E31313' }}
                        />
                        <label htmlFor="informarPercentualEntrada" style={{ fontWeight: 500, color: '#334155', fontSize: 14, cursor: 'pointer' }}>
                          Informar % de Entrada
                        </label>
                      </div>
                      
                      {/* Campo para digitar percentual */}
                      {informarPercentualEntrada && (
                        <div style={{ marginLeft: 24, display: 'flex', alignItems: 'center', gap: 8 }}>
                          <label style={{ fontSize: 14, color: '#334155', minWidth: 100 }}>
                            % de Entrada:
                          </label>
                          <input
                            type="text"
                            value={percentualEntrada}
                            onChange={handlePercentualEntradaChange}
                            placeholder="Ex: 10"
                            style={{
                              width: 80,
                              padding: '6px 8px',
                              border: '1px solid #ccc',
                              borderRadius: 4,
                              fontSize: 14,
                              textAlign: 'center'
                            }}
                            maxLength="5"
                          />
                          <span style={{ fontSize: 14, color: '#666' }}>%</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Exibir cálculos se marcado */}
                  {barter && (
                    <div style={{ marginBottom: 16 }}>
                      <h3 style={{ fontSize: 17, color: '#1e293b', margin: '8px 0 12px 0' }}>Recurso Próprio em Barter:</h3>
                      {resultados
                        .slice()
                        .sort((a, b) => ordemBancos.indexOf(a.banco) - ordemBancos.indexOf(b.banco))
                        .map((r, i) => {
                          // Calcular valor do recurso próprio ajustado com entrada em dinheiro
                          const valorRecursoProprioAjustado = calcularValorRecursoProprioAjustado(
                            r.valor_recurso_proprio, 
                            parseFloat(valorExibido || valorBem || 0), 
                            percentualEntrada, 
                            informarPercentualEntrada
                          );
                          
                          const valoresCorrigidos = calcularValoresCorrigidos(valorRecursoProprioAjustado);
                          return (
                            <div key={i} style={{ marginBottom: 20, padding: 15, background: '#F7F7F7', borderRadius: 8, fontSize: 15 }}>
                              <div style={{ fontWeight: 600, color: '#E31313', marginBottom: 8, fontSize: 16 }}>{r.banco}</div>
                              
                              {/* Mostrar informações da entrada se aplicável */}
                              {informarPercentualEntrada && percentualEntrada && (
                                <div style={{ marginBottom: 12, padding: 8, background: '#E8F5E8', borderRadius: 4, border: '1px solid #4CAF50' }}>
                                  <div style={{ fontSize: 13, color: '#2E7D32', fontWeight: 500 }}>
                                    💰 Entrada em dinheiro: {percentualEntrada}% = R$ {((parseFloat(valorExibido || valorBem || 0) * parseFloat(percentualEntrada)) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </div>
                                  <div style={{ fontSize: 12, color: '#388E3C', marginTop: 2 }}>
                                    Valor para Barter: R$ {valorRecursoProprioAjustado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </div>
                          </div>
                              )}
                              


                              {/* Tabela de valores corrigidos */}
                              <div style={{ marginTop: 15 }}>
                                <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: 8, fontSize: 14 }}>
                                  Correção por Prazo de Pagamento:
                                </div>
                                <div style={{ overflowX: 'auto' }}>
                                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                    <thead>
                                      <tr style={{ background: '#666666', color: '#fff' }}>
                                        <th style={{ padding: '6px 4px', textAlign: 'center', border: '1px solid #ccc' }}>Dias</th>
                                        {valoresCorrigidos.map((v, idx) => (
                                          <th key={idx} style={{ padding: '6px 4px', textAlign: 'center', border: '1px solid #ccc', minWidth: 60 }}>
                                            {v.mes}
                                          </th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      <tr style={{ background: '#fff' }}>
                                        <td style={{ padding: '6px 4px', textAlign: 'center', border: '1px solid #ccc', fontWeight: 600, fontSize: 11 }}>
                                          Soja
                                        </td>
                                        {valoresCorrigidos.map((v, idx) => (
                                          <td key={idx} style={{ padding: '6px 4px', textAlign: 'center', border: '1px solid #ccc', fontSize: 11 }}>
                                            {formatSacas(cotacoes.soja?.valor ? v.valorCorrigido / cotacoes.soja.valor : null)}
                                          </td>
                                        ))}
                                      </tr>
                                      <tr style={{ background: '#f9f9f9' }}>
                                        <td style={{ padding: '6px 4px', textAlign: 'center', border: '1px solid #ccc', fontWeight: 600, fontSize: 11 }}>
                                          Milho
                                        </td>
                                        {valoresCorrigidos.map((v, idx) => (
                                          <td key={idx} style={{ padding: '6px 4px', textAlign: 'center', border: '1px solid #ccc', fontSize: 11 }}>
                                            {formatSacas(cotacoes.milho?.valor ? v.valorCorrigido / cotacoes.milho.valor : null)}
                                          </td>
                                        ))}
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </div>

                              {(cotacoes.soja?.data_hora || cotacoes.milho?.data_hora) && (
                                <div style={{ fontSize: '11px', color: '#888', marginTop: '8px' }}>
                                  📊 Cotações atualizadas: {new Date(cotacoes.soja?.data_hora || cotacoes.milho?.data_hora).toLocaleString('pt-BR')}
                    </div>
                  )}
                </div>
                          );
                        })}
                </div>
                  )}
                  
                  {/* Checkbox Operação CPR */}
                  <div style={{ margin: '32px 0 8px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      id="cpr"
                      checked={cpr}
                      onChange={e => setCpr(e.target.checked)}
                      style={{ accentColor: '#E31313' }}
                    />
                    <label htmlFor="cpr" style={{ fontWeight: 600, color: '#334155', fontSize: 16, cursor: 'pointer' }}>
                      Operação CPR
                    </label>
                  </div>
                  <div style={{ marginBottom: 8, textAlign: 'left' }}>
                    <span style={{ color: '#666', fontSize: 14, fontStyle: 'italic' }}>
                      Sujeito a análise de crédito e aprovação pela instituição.
                    </span>
                  </div>
                  <div style={{ marginBottom: 16, textAlign: 'left' }}>
                    <span style={{ color: '#E31313', fontWeight: 700, fontSize: 14 }}>
                      ** Valores aproximados e sujeitos a alterações até a data da contratação.
                    </span>
                  </div>
                  
                  {/* Exibir cálculos CPR se marcado */}
                  {cpr && (
                    <div style={{ marginBottom: 16 }}>
                      <h3 style={{ fontSize: 17, color: '#1e293b', margin: '8px 0 12px 0' }}>Operação CPR:</h3>
                      
                      {/* Checkbox para informar percentual de entrada CPR */}
                      <div style={{ marginBottom: 15 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                          <input
                            type="checkbox"
                            id="informarPercentualEntradaCPR"
                            checked={informarPercentualEntradaCPR}
                            onChange={e => {
                              setInformarPercentualEntradaCPR(e.target.checked);
                              if (!e.target.checked) {
                                setPercentualEntradaCPR('');
                              }
                            }}
                            style={{ accentColor: '#E31313' }}
                          />
                          <label htmlFor="informarPercentualEntradaCPR" style={{ fontWeight: 500, color: '#334155', fontSize: 14, cursor: 'pointer' }}>
                            Informar % de Entrada
                          </label>
                        </div>
                        
                        {/* Campo para digitar percentual */}
                        {informarPercentualEntradaCPR && (
                          <div style={{ marginLeft: 24, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <label style={{ fontSize: 14, color: '#334155', minWidth: 100 }}>
                              % de Entrada:
                            </label>
                            <input
                              type="text"
                              value={percentualEntradaCPR}
                              onChange={handlePercentualEntradaCPRChange}
                              placeholder="Ex: 10"
                              style={{
                                width: 80,
                                padding: '6px 8px',
                                border: '1px solid #ccc',
                                borderRadius: 4,
                                fontSize: 14,
                                textAlign: 'center'
                              }}
                              maxLength="5"
                            />
                            <span style={{ fontSize: 14, color: '#666' }}>%</span>
                          </div>
                        )}
                      </div>

                      {resultados
                  .slice()
                  .sort((a, b) => ordemBancos.indexOf(a.banco) - ordemBancos.indexOf(b.banco))
                        .map((r, i) => {
                          const valorRecursoProprioAjustadoCPR = calcularValorRecursoProprioAjustado(r.valor_recurso_proprio, valorExibido || valorBem || 0, percentualEntradaCPR, informarPercentualEntradaCPR);
                          const valoresCPR = calcularOperacaoCPR(valorRecursoProprioAjustadoCPR);
                          // Filtrar apenas os meses que têm amortização
                          const mesesComAmortizacao = valoresCPR.filter(v => v.amortizacao > 0);
                          
                                                    return (
                            <div key={i} style={{ marginBottom: 20, padding: 15, background: '#F7F7F7', borderRadius: 8, fontSize: 15 }}>
                              <div style={{ fontWeight: 600, color: '#E31313', marginBottom: 8, fontSize: 16 }}>{r.banco}</div>
                              
                              {/* Mostrar informações da entrada se aplicável */}
                              {informarPercentualEntradaCPR && percentualEntradaCPR && (
                                <div style={{ marginBottom: 12, padding: 8, background: '#E8F5E8', borderRadius: 4, border: '1px solid #4CAF50' }}>
                                  <div style={{ fontSize: 13, color: '#2E7D32', fontWeight: 500 }}>
                                    💰 Entrada em dinheiro: {percentualEntradaCPR}% = R$ {((parseFloat(valorExibido || valorBem || 0) * parseFloat(percentualEntradaCPR)) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                  </div>
                                  <div style={{ fontSize: 12, color: '#388E3C', marginTop: 2 }}>
                                    Valor para CPR: R$ {valorRecursoProprioAjustadoCPR.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                  </div>
                                </div>
                              )}
                              
                              {/* Tabela de valores CPR */}
                              <div style={{ marginTop: 15 }}>
                                <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: 8, fontSize: 14 }}>
                                  Cálculo CPR - Valor Financiado: R$ {formatMilhar(valorRecursoProprioAjustadoCPR)}
                    </div>
                                <div style={{ overflowX: 'auto' }}>
                                  <table style={{ width: 'auto', borderCollapse: 'collapse', fontSize: 12 }}>
                                    <thead>
                                      <tr style={{ background: '#666666', color: '#fff' }}>
                                        <th style={{ padding: '6px 12px', textAlign: 'center', border: '1px solid #ccc', whiteSpace: 'nowrap' }}>Data</th>
                                        <th style={{ padding: '6px 12px', textAlign: 'center', border: '1px solid #ccc', whiteSpace: 'nowrap' }}>Amortização</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {mesesComAmortizacao.map((v, idx) => (
                                        <tr key={idx} style={{ background: idx % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                                          <td style={{ padding: '6px 12px', textAlign: 'center', border: '1px solid #ccc', fontSize: 11, whiteSpace: 'nowrap' }}>
                                            {v.data.toLocaleDateString('pt-BR')}
                                          </td>
                                          <td style={{ padding: '6px 12px', textAlign: 'right', border: '1px solid #ccc', fontSize: 11, whiteSpace: 'nowrap' }}>
                                            R$ {formatMilhar(v.amortizacao)}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                  </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  )}

                  {/* Botão expansível - Operações com o valor do pedido */}
                  <div style={{ margin: '48px 0 24px 0' }}>
                    <button
                      onClick={() => setMostrarOperacoesPedido(!mostrarOperacoesPedido)}
                      style={{
                        backgroundColor: 'transparent',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        fontSize: 18,
                        color: '#1e293b',
                        fontWeight: 600,
                        textAlign: 'left'
                      }}
                    >
                      <span style={{ 
                        fontSize: 16, 
                        transform: mostrarOperacoesPedido ? 'rotate(90deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                      }}>
                        ▶
                      </span>
                      Operações com o Valor do Pedido:
                    </button>
                    <div style={{ marginTop: 4, marginLeft: 24 }}>
                      <span style={{ fontSize: 14, color: '#666', fontStyle: 'italic' }}>
                        Clique para expandir as opções.
                      </span>
                    </div>
                  </div>

                  {/* Seção expansível das operações */}
                  {mostrarOperacoesPedido && (
                    <div style={{ marginLeft: 16 }}>
                      {/* Checkbox Operação Barter - Valor do Pedido */}
                  <div style={{ margin: '32px 0 8px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      id="barterValorPedido"
                      checked={barterValorPedido}
                      onChange={e => {
                        setBarterValorPedido(e.target.checked);
                        // Reset percentual quando desmarca barter
                        if (!e.target.checked) {
                          setInformarPercentualEntradaBarterPedido(false);
                          setPercentualEntradaBarterPedido('');
                        }
                      }}
                      style={{ accentColor: '#E31313' }}
                    />
                    <label htmlFor="barterValorPedido" style={{ fontWeight: 600, color: '#334155', fontSize: 16, cursor: 'pointer' }}>
                      Operação Barter - Valor do Pedido
                    </label>
                  </div>
                  
                  <div style={{ marginBottom: 8, textAlign: 'left' }}>
                    <span style={{ color: '#666', fontSize: 14, fontStyle: 'italic' }}>
                      Sujeito a análise de crédito e aprovação pela instituição.
                    </span>
                  </div>
                  <div style={{ marginBottom: 16, textAlign: 'left' }}>
                    <span style={{ color: '#E31313', fontWeight: 700, fontSize: 14 }}>
                      ** Valores aproximados e sujeitos a alterações até a data da contratação.
                    </span>
                  </div>
                  
                  {/* Checkbox e campo para percentual de entrada - só aparece se Barter Valor do Pedido estiver marcado */}
                  {barterValorPedido && (
                    <div style={{ marginLeft: 24, marginBottom: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <input
                          type="checkbox"
                          id="informarPercentualEntradaBarterPedido"
                          checked={informarPercentualEntradaBarterPedido}
                          onChange={e => {
                            setInformarPercentualEntradaBarterPedido(e.target.checked);
                            if (!e.target.checked) {
                              setPercentualEntradaBarterPedido('');
                            }
                          }}
                          style={{ accentColor: '#E31313' }}
                        />
                        <label htmlFor="informarPercentualEntradaBarterPedido" style={{ fontWeight: 500, color: '#334155', fontSize: 14, cursor: 'pointer' }}>
                          Informar % de Entrada
                        </label>
                      </div>
                      
                      {/* Campo para digitar percentual */}
                      {informarPercentualEntradaBarterPedido && (
                        <div style={{ marginLeft: 24, display: 'flex', alignItems: 'center', gap: 8 }}>
                          <label style={{ fontSize: 14, color: '#334155', minWidth: 100 }}>
                            % de Entrada:
                          </label>
                          <input
                            type="text"
                            value={percentualEntradaBarterPedido}
                            onChange={handlePercentualEntradaBarterPedidoChange}
                            placeholder="Ex: 10"
                            style={{
                              width: 80,
                              padding: '6px 8px',
                              border: '1px solid #ccc',
                              borderRadius: 4,
                              fontSize: 14,
                              textAlign: 'center'
                            }}
                            maxLength="5"
                          />
                          <span style={{ fontSize: 14, color: '#666' }}>%</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Exibir cálculos se marcado */}
                  {barterValorPedido && (
                    <div style={{ marginBottom: 16 }}>
                      <h3 style={{ fontSize: 17, color: '#1e293b', margin: '8px 0 12px 0' }}>Valor do Pedido em Barter:</h3>
                      {(() => {
                        // Calcular valor do recurso próprio ajustado com entrada em dinheiro
                        const valorDigitadoAjustado = calcularValorRecursoProprioAjustado(
                          parseFloat(valorExibido || valorBem || 0), 
                          parseFloat(valorExibido || valorBem || 0), 
                          percentualEntradaBarterPedido, 
                          informarPercentualEntradaBarterPedido
                        );
                        
                        const valoresCorrigidos = calcularValoresCorrigidos(valorDigitadoAjustado);
                        
                        return (
                          <div style={{ marginBottom: 20, padding: 15, background: '#F7F7F7', borderRadius: 8, fontSize: 15 }}>
                            {/* Mostrar informações da entrada se aplicável */}
                            {informarPercentualEntradaBarterPedido && percentualEntradaBarterPedido && (
                              <div style={{ marginBottom: 12, padding: 8, background: '#E8F5E8', borderRadius: 4, border: '1px solid #4CAF50' }}>
                                <div style={{ fontSize: 13, color: '#2E7D32', fontWeight: 500 }}>
                                  💰 Entrada em dinheiro: {percentualEntradaBarterPedido}% = R$ {((parseFloat(valorExibido || valorBem || 0) * parseFloat(percentualEntradaBarterPedido)) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                </div>
                                <div style={{ fontSize: 12, color: '#388E3C', marginTop: 2 }}>
                                  Valor para Barter: R$ {valorDigitadoAjustado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                </div>
                              </div>
                            )}
                            
                            {/* Tabela de valores corrigidos */}
                            <div style={{ marginTop: 15 }}>
                              <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: 8, fontSize: 14 }}>
                                Correção por Prazo de Pagamento:
                              </div>
                              <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                  <thead>
                                    <tr style={{ background: '#666666', color: '#fff' }}>
                                      <th style={{ padding: '6px 4px', textAlign: 'center', border: '1px solid #ccc' }}>Dias</th>
                                      {valoresCorrigidos.map((v, idx) => (
                                        <th key={idx} style={{ padding: '6px 4px', textAlign: 'center', border: '1px solid #ccc', minWidth: 60 }}>
                                          {v.mes}
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr style={{ background: '#fff' }}>
                                      <td style={{ padding: '6px 4px', textAlign: 'center', border: '1px solid #ccc', fontWeight: 600, fontSize: 11 }}>
                                        Soja
                                      </td>
                                      {valoresCorrigidos.map((v, idx) => (
                                        <td key={idx} style={{ padding: '6px 4px', textAlign: 'center', border: '1px solid #ccc', fontSize: 11 }}>
                                          {formatSacas(cotacoes.soja?.valor ? v.valorCorrigido / cotacoes.soja.valor : null)}
                                        </td>
                                      ))}
                                    </tr>
                                    <tr style={{ background: '#f9f9f9' }}>
                                      <td style={{ padding: '6px 4px', textAlign: 'center', border: '1px solid #ccc', fontWeight: 600, fontSize: 11 }}>
                                        Milho
                                      </td>
                                      {valoresCorrigidos.map((v, idx) => (
                                        <td key={idx} style={{ padding: '6px 4px', textAlign: 'center', border: '1px solid #ccc', fontSize: 11 }}>
                                          {formatSacas(cotacoes.milho?.valor ? v.valorCorrigido / cotacoes.milho.valor : null)}
                                        </td>
                                      ))}
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>

                            {(cotacoes.soja?.data_hora || cotacoes.milho?.data_hora) && (
                              <div style={{ fontSize: '11px', color: '#888', marginTop: '8px' }}>
                                📊 Cotações atualizadas: {new Date(cotacoes.soja?.data_hora || cotacoes.milho?.data_hora).toLocaleString('pt-BR')}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                  
                  {/* Checkbox Operação CPR - Valor do Pedido */}
                  <div style={{ margin: '32px 0 8px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      id="cprValorPedido"
                      checked={cprValorPedido}
                      onChange={e => setCprValorPedido(e.target.checked)}
                      style={{ accentColor: '#E31313' }}
                    />
                    <label htmlFor="cprValorPedido" style={{ fontWeight: 600, color: '#334155', fontSize: 16, cursor: 'pointer' }}>
                      Operação CPR - Valor do Pedido
                    </label>
                  </div>
                  <div style={{ marginBottom: 8, textAlign: 'left' }}>
                    <span style={{ color: '#666', fontSize: 14, fontStyle: 'italic' }}>
                      Sujeito a análise de crédito e aprovação pela instituição.
                    </span>
                  </div>
                  <div style={{ marginBottom: 16, textAlign: 'left' }}>
                    <span style={{ color: '#E31313', fontWeight: 700, fontSize: 14 }}>
                      ** Valores aproximados e sujeitos a alterações até a data da contratação.
                    </span>
                  </div>
                  
                  {/* Exibir cálculos CPR se marcado */}
                  {cprValorPedido && (
                    <div style={{ marginBottom: 16 }}>
                      <h3 style={{ fontSize: 17, color: '#1e293b', margin: '8px 0 12px 0' }}>Operação CPR - Valor do Pedido:</h3>
                      
                      {/* Checkbox para informar percentual de entrada CPR */}
                      <div style={{ marginBottom: 15 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                          <input
                            type="checkbox"
                            id="informarPercentualEntradaCPRPedido"
                            checked={informarPercentualEntradaCPRPedido}
                            onChange={e => {
                              setInformarPercentualEntradaCPRPedido(e.target.checked);
                              if (!e.target.checked) {
                                setPercentualEntradaCPRPedido('');
                              }
                            }}
                            style={{ accentColor: '#E31313' }}
                          />
                          <label htmlFor="informarPercentualEntradaCPRPedido" style={{ fontWeight: 500, color: '#334155', fontSize: 14, cursor: 'pointer' }}>
                            Informar % de Entrada
                          </label>
                        </div>
                        
                        {/* Campo para digitar percentual */}
                        {informarPercentualEntradaCPRPedido && (
                          <div style={{ marginLeft: 24, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <label style={{ fontSize: 14, color: '#334155', minWidth: 100 }}>
                              % de Entrada:
                            </label>
                            <input
                              type="text"
                              value={percentualEntradaCPRPedido}
                              onChange={handlePercentualEntradaCPRPedidoChange}
                              placeholder="Ex: 10"
                              style={{
                                width: 80,
                                padding: '6px 8px',
                                border: '1px solid #ccc',
                                borderRadius: 4,
                                fontSize: 14,
                                textAlign: 'center'
                              }}
                              maxLength="5"
                            />
                            <span style={{ fontSize: 14, color: '#666' }}>%</span>
                          </div>
                        )}
                      </div>

                      {(() => {
                        const valorDigitadoAjustadoCPR = calcularValorRecursoProprioAjustado(parseFloat(valorExibido || valorBem || 0), parseFloat(valorExibido || valorBem || 0), percentualEntradaCPRPedido, informarPercentualEntradaCPRPedido);
                        const valoresCPR = calcularOperacaoCPR(valorDigitadoAjustadoCPR);
                        // Filtrar apenas os meses que têm amortização
                        const mesesComAmortizacao = valoresCPR.filter(v => v.amortizacao > 0);
                        
                        return (
                          <div style={{ marginBottom: 20, padding: 15, background: '#F7F7F7', borderRadius: 8, fontSize: 15 }}>
                            {/* Mostrar informações da entrada se aplicável */}
                            {informarPercentualEntradaCPRPedido && percentualEntradaCPRPedido && (
                              <div style={{ marginBottom: 12, padding: 8, background: '#E8F5E8', borderRadius: 4, border: '1px solid #4CAF50' }}>
                                <div style={{ fontSize: 13, color: '#2E7D32', fontWeight: 500 }}>
                                  💰 Entrada em dinheiro: {percentualEntradaCPRPedido}% = R$ {((parseFloat(valorExibido || valorBem || 0) * parseFloat(percentualEntradaCPRPedido)) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                </div>
                                <div style={{ fontSize: 12, color: '#388E3C', marginTop: 2 }}>
                                  Valor para CPR: R$ {valorDigitadoAjustadoCPR.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                </div>
                              </div>
                            )}
                            
                            {/* Tabela de valores CPR */}
                            <div style={{ marginTop: 15 }}>
                              <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: 8, fontSize: 14 }}>
                                Cálculo CPR - Valor Financiado: R$ {formatMilhar(valorDigitadoAjustadoCPR)}
                              </div>
                              <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: 'auto', borderCollapse: 'collapse', fontSize: 12 }}>
                                  <thead>
                                    <tr style={{ background: '#666666', color: '#fff' }}>
                                      <th style={{ padding: '6px 12px', textAlign: 'center', border: '1px solid #ccc', whiteSpace: 'nowrap' }}>Data</th>
                                      <th style={{ padding: '6px 12px', textAlign: 'center', border: '1px solid #ccc', whiteSpace: 'nowrap' }}>Amortização</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {mesesComAmortizacao.map((v, idx) => (
                                      <tr key={idx} style={{ background: idx % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                                        <td style={{ padding: '6px 12px', textAlign: 'center', border: '1px solid #ccc', fontSize: 11, whiteSpace: 'nowrap' }}>
                                          {v.data.toLocaleDateString('pt-BR')}
                                        </td>
                                        <td style={{ padding: '6px 12px', textAlign: 'right', border: '1px solid #ccc', fontSize: 11, whiteSpace: 'nowrap' }}>
                                          R$ {formatMilhar(v.amortizacao)}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                    </div>
                  )}
                </div>
            )}
          </div>
        </div>
      )}
      
      {/* Toast de Aviso */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          background: '#fff',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          maxWidth: '350px',
          zIndex: 1000,
          border: '2px solid #D4AF37',
          animation: 'slideInRight 0.5s ease-out'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px'
          }}>
            <div style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              background: '#D4AF37',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              marginTop: '2px'
            }}>
              <span style={{ color: '#fff', fontSize: '14px', fontWeight: 'bold' }}>!</span>
            </div>
            <div style={{ flex: 1 }}>
              <h4 style={{
                color: '#1e293b',
                fontWeight: 700,
                fontSize: '16px',
                margin: '0 0 8px 0'
              }}>
                Aviso
              </h4>
              <p style={{
                color: '#334155',
                fontSize: '14px',
                lineHeight: '1.4',
                margin: 0,
                fontWeight: 500
              }}>
                ** Valores aproximados e sujeitos a alterações até a data da contratação.
              </p>
            </div>
            <button
              onClick={() => setShowModal(false)}
              style={{
                background: 'none',
                border: 'none',
                color: '#666',
                fontSize: '18px',
                cursor: 'pointer',
                padding: '0',
                marginLeft: '8px',
                fontWeight: 'bold'
              }}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
